import { executeWithNetworkRetries } from "@metriport/shared";
import { z } from "zod";
import { apiOSS } from "./apiOSS";

const PATIENT_URL = `/medical/v1/patient`;

const referenceSchema = z.object({
  reference: z.string(),
});

const periodSchema = z.object({
  start: z.string(),
  end: z.string(),
});

const codeableConceptSchema = z.object({
  coding: z.array(
    z.object({
      system: z.string(),
      code: z.string(),
    })
  ),
});

const measureReportPopulationSchema = z.object({
  code: codeableConceptSchema,
  count: z.number(),
});

const measureReportGroupSchema = z.object({
  id: z.string(),
  code: codeableConceptSchema,
  population: z.array(measureReportPopulationSchema),
});

const measureReportSchema = z.object({
  resourceType: z.literal("MeasureReport"),
  id: z.string(),
  status: z.string(),
  type: z.string(),
  measure: z.string(),
  subject: referenceSchema,
  period: periodSchema,
  group: z.array(measureReportGroupSchema),
});

const careGapSchema = z.object({
  id: z.string(),
  cxId: z.string(),
  patientId: z.string(),
  measureName: z.string(),
  jobId: z.string(),
  measureReport: measureReportSchema,
  supportingEvidence: z.record(z.unknown()).optional(),
  lastRun: z.string().transform(str => new Date(str)),
  createdAt: z.string().transform(str => new Date(str)),
  updatedAt: z.string().transform(str => new Date(str)),
  version: z.number(),
  eTag: z.string().optional(),
});

const careGapsResponseSchema = z.object({
  careGaps: z.array(careGapSchema),
});

type Reference = z.infer<typeof referenceSchema>;
type Period = z.infer<typeof periodSchema>;
type CodeableConcept = z.infer<typeof codeableConceptSchema>;
type MeasureReportPopulation = z.infer<typeof measureReportPopulationSchema>;
type MeasureReportGroup = z.infer<typeof measureReportGroupSchema>;
type MeasureReport = z.infer<typeof measureReportSchema>;
type CareGap = z.infer<typeof careGapSchema>;
type CareGapsResponse = z.infer<typeof careGapsResponseSchema>;

/**
 * Fetches care gaps for a patient from the API.
 * Returns latest care gaps grouped by measure name.
 */
export async function getCareGaps(patientId: string): Promise<CareGapsResponse> {
  const resp = await executeWithNetworkRetries(
    () => apiOSS.get(`${PATIENT_URL}/${patientId}/care-gaps`),
    { maxAttempts: 5, initialDelay: 100 }
  );

  const careGapsData = careGapsResponseSchema.parse(resp.data);

  return careGapsData;
}

export type {
  CareGap,
  CareGapsResponse,
  MeasureReport,
  MeasureReportGroup,
  MeasureReportPopulation,
  CodeableConcept,
  Period,
  Reference,
};
